/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { z } from "zod";

import { CSELanguageCode } from "./types";

import type {
	CosmosSearchFilterStrict,
	CosmosSearchItem,
	CosmosSearchResponse,
	CSESearchPageMapAsset,
	GoogleDeResponse,
} from "./types";
import type { CosmosAssetFragment, SearchFilterFragment } from "@cosmos/hygraph-schema";

const domainsAllowed = new RegExp("(unrealengine.com|epicgames.com|twinmotion.com)");
const filenamesToSkip = new RegExp("(placeholder|logo|UE_Feed_Migration_Image|social-media-image)");

export const cseImagesToAsset = (cse_images?: CSESearchPageMapAsset[]): Partial<CosmosAssetFragment> | undefined => {
	if (
		cse_images &&
		cse_images?.length > 0 &&
		cse_images[0] &&
		cse_images[0].src &&
		domainsAllowed.test(cse_images[0].src) &&
		!filenamesToSkip.test(cse_images[0].src)
	) {
		const width = parseInt(cse_images[0].width || "");
		const height = parseInt(cse_images[0].height || "");
		return {
			url: cse_images[0].src,
			width: width > 0 ? width : undefined,
			height: height > 0 ? height : undefined,
		};
	}

	return undefined;
};

export const processItems = (data: GoogleDeResponse): CosmosSearchResponse => {
	if (!data?.results?.length) {
		return {
			items: [],
			total: 0,
		};
	}

	const items = data?.results?.filter((result) => result?.document?.derivedStructData?.title) || [];
	return {
		items: items?.map((result) => {
			const item = result?.document?.derivedStructData;
			return {
				id: result?.document?.id,
				title: { html: item?.htmlTitle, text: item?.title },
				href: item?.link,
				formattedHref: item?.htmlFormattedUrl,
				desc: item?.snippets?.[0].htmlSnippet,
				asset: cseImagesToAsset(item?.pagemap?.cse_image),
			} as CosmosSearchItem;
		}),
		total: items?.length || 0,
	};
};

export const getFilterPayload = (
	filter?: string,
	defaultSearchFilter?: string | null,
	searchFilters?: SearchFilterFragment[] | null,
	defaultDomain?: string,
): string => {
	const defaultFilter = defaultSearchFilter || defaultDomain || "";

	if (!filter) {
		return defaultFilter;
	}

	const matchingFilter = (searchFilters || [])
		.filter((item): item is CosmosSearchFilterStrict => !!item.label && !!item.value)
		.find((item) => item.label.toLowerCase() === filter.toLowerCase());

	return matchingFilter?.value || defaultFilter;
};

export const searchEnvSchema = z.object({
	EPIC_SERVICE_GOOGLE_SEARCH_APP_ID: z.string(),
	VERTEX_SEARCH_API_KEY: z.string(),
	EPIC_SERVICE_GOOGLE_SEARCH_PROJECT_ID: z.string(),
	EPIC_SERVICE_GOOGLE_SEARCH_DOMAIN: z.string(),
});

export type SearchEnvSchema = z.infer<typeof searchEnvSchema>;

export const searchParamsSchema = z.object({
	query: z.string(),
	locale: z.string().default("en-US"),
	filter: z.string().optional(),
	slug: z.string(),
	preview: z.string().optional(),
});

export type SearchParamsSchema = z.infer<typeof searchParamsSchema>;

export const processLocale = (s: string): CSELanguageCode => {
	const shortCode = ["zh-TW", "zh-CN"].includes(s) ? s : s.slice(0, 2).toLowerCase();

	if (CSELanguageCode[shortCode as keyof typeof CSELanguageCode]) {
		return CSELanguageCode[shortCode as keyof typeof CSELanguageCode];
	}
	return CSELanguageCode.en;
};
